import { useAPIGetHistorySelection } from 'api/selection/useAPISelection'
import { useTracker } from 'api/tracker/useAPITracker'
import Image from 'next/image'
import Link from 'next/link'
import { ActiveSelection, HistorySelection } from 'types/selections'

import { Button } from '@wartek-id/button'
import { numberUtils } from '@wartek-id/fe-toolbox'
import { Icon } from '@wartek-id/icon'
import { Separator } from '@wartek-id/separator'
import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text'

import { formatDate } from 'utils/date'
import { useRoles } from 'utils/hooks'
import { useRegionContext } from 'utils/hooks/useRegion'

import { getIsSelectionEndedAfterKemendagriCutoff } from 'app/HistorySelection/helpers'
import useActiveSelection from 'app/hooks/useActiveSelection'
import useOverviewSupplyDemand from 'app/hooks/useOverviewSupplyDemand'
import ButtonKSSelectionSettings from 'app/KS/components/ButtonKSSelectionSettings'

import CircularProgress from 'components/CircularProgress'
import DataOverview from 'components/DataOverview'
import Panel from 'components/Panel'

const isLaporKemendagriEnabled =
  process.env.NEXT_PUBLIC_TOGGLE_KS_SELECTIONS_LAPOR_KEMENDAGRI_ENABLED ===
  'true'

const PATH_START_SELECTION = '/ks/demand/update'

interface BaseSectionProps {
  activeSelection: ActiveSelection
}

const SeleksiKSNotStarted = () => {
  const { overviewCandidates, overviewSchools } = useOverviewSupplyDemand()
  const { satuEventTrack } = useTracker()

  const onClickStartSelection = () => {
    satuEventTrack({
      event_name: 'click_start_selection',
    })
  }

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="max-w-[560px]">
        <Text color="subdued" className="text-center">
          Anda dapat mulai mempersiapkan seleksi Kepala Sekolah dengan cek dan
          perbarui data di dasbor agar sesuai dengan data daerah Anda.
        </Text>
      </div>

      <div className="mx-auto w-[420px]">
        <DataOverview
          centered
          overviews={[
            {
              title: 'Kebutuhan KS',
              value: numberUtils.delimit(
                overviewSchools?.totalSchoolDemand ?? 0
              ),
            },
            {
              title: 'BCKS GP/diklat',
              value: numberUtils.delimit(
                overviewCandidates?.totalPreferred ?? 0
              ),
            },
          ]}
        />
      </div>

      <div className="mx-auto flex gap-3">
        <Link href={PATH_START_SELECTION} passHref>
          <Button size="lg" onClick={onClickStartSelection}>
            Persiapkan seleksi
            <Icon color="inverse" className="ml-1">
              arrow_forward
            </Icon>
          </Button>
        </Link>

        <Link
          href="https://pusatinformasi.pengangkatan-ksps.kemdikbud.go.id/hc/en-gb"
          passHref
        >
          <Button size="lg" color="white">
            Baca info
          </Button>
        </Link>
      </div>
    </div>
  )
}

const SeleksiKSDocumentVerification = ({
  activeSelection,
}: BaseSectionProps) => {
  const { refetchActiveSelection } = useActiveSelection()
  const startDate = formatDate(activeSelection.startDate)
  const endDate = formatDate(activeSelection.endDate)
  const percentageBerkasDiterima = Math.ceil(
    (activeSelection.totalDocumentSubmitted / activeSelection.totalCandidates) *
      100
  )
  const percentageBerkasSelesaiDicek = Math.ceil(
    (activeSelection.totalDocumentVerified /
      activeSelection.totalDocumentSubmitted) *
      100
  )

  return (
    <div className="flex flex-col items-center gap-4">
      <Text color="subdued" data-testid="periode-seleksi">
        Periode Seleksi: {startDate} - {endDate}
      </Text>

      <Panel
        className="max-w-[320px] p-6"
        isActive={activeSelection.state === 'DOCUMENT_VERIFICATION'}
      >
        <Text variant="heading-sm" className="text-center">
          Cek Berkas BCKS
        </Text>
        <div className="mt-1 flex items-center justify-center gap-3">
          <Text
            variant="subheading"
            className="!text-[#2C70E5]"
            data-testid="total-berkas-diterima"
          >
            {isNaN(percentageBerkasDiterima) ? 0 : percentageBerkasDiterima}%
          </Text>
          <Text color="subdued">berkas diterima</Text>
        </div>
        <div className="mt-1 flex items-center justify-center gap-3">
          <Text
            variant="subheading"
            className="!text-[#2C70E5]"
            data-testid="total-berkas-selesai-dicek"
          >
            {isNaN(percentageBerkasSelesaiDicek)
              ? 0
              : percentageBerkasSelesaiDicek}
            %
          </Text>
          <Text color="subdued">berkas selesai dicek</Text>
        </div>
      </Panel>

      <div className="flex gap-2">
        <ButtonKSSelectionSettings
          selection={activeSelection}
          onSuccessCancelSelection={refetchActiveSelection}
        />

        <Link href={`/selections/${activeSelection.id}`} passHref>
          <Button size="lg">
            Lanjutkan seleksi
            <Icon color="inverse" className="ml-1">
              arrow_forward
            </Icon>
          </Button>
        </Link>
      </div>
    </div>
  )
}

const SeleksiKSPairing = ({ activeSelection }: BaseSectionProps) => {
  const { refetchActiveSelection } = useActiveSelection()
  const startDate = formatDate(activeSelection.startDate)
  const endDate = formatDate(activeSelection.endDate)

  const percentagePemasangan = Math.ceil(
    (activeSelection.totalDemandPaired / activeSelection.totalDemand) * 100
  )

  return (
    <div className="flex flex-col items-center gap-4">
      <Text color="subdued" data-testid="periode-seleksi">
        Periode Seleksi: {startDate} - {endDate}
      </Text>

      <div className="max-w-[320px]">
        <CircularProgress
          title="Pasangkan BCKS"
          description={`${activeSelection.totalDemandPaired} dari ${activeSelection.totalDemand} sekolah`}
          value={isNaN(percentagePemasangan) ? 0 : percentagePemasangan}
          isActive={activeSelection.state === 'PAIRING'}
        />
      </div>

      <div className="flex gap-2">
        <ButtonKSSelectionSettings
          selection={activeSelection}
          onSuccessCancelSelection={refetchActiveSelection}
        />

        <Link href={`/selections/${activeSelection.id}`} passHref>
          <Button size="lg">
            Lanjutkan seleksi
            <Icon color="inverse" className="ml-1">
              arrow_forward
            </Icon>
          </Button>
        </Link>
      </div>
    </div>
  )
}

const SeleksiKSFinalization = ({ activeSelection }: BaseSectionProps) => {
  return (
    <div className="flex justify-center">
      <div className="flex max-w-[560px] flex-col items-center gap-4">
        <Image
          alt="Ilustrasi Finalisasi Seleksi Kepala Sekolah"
          src="/images/img-flow-process-document.png"
          width={84}
          height={84}
        />

        <div className="flex flex-col gap-1">
          <Text variant="heading-md" className="text-center">
            Tahap terakhir: Finalisasi
          </Text>
          <Text color="subdued" className="text-center">
            Pastikan sekolah dipasangkan dengan CKS yang telah disetujui Tim
            Pertimbangan sebagai hasil akhir seleksi.
          </Text>
        </div>

        <Link href={`/selections/${activeSelection.id}`} passHref>
          <Button size="lg">
            Selesaikan seleksi
            <Icon color="inverse" className="ml-1">
              arrow_forward
            </Icon>
          </Button>
        </Link>
      </div>
    </div>
  )
}

const SeleksiKSUnggahSK = ({ activeSelection }: BaseSectionProps) => {
  const { isDinasUser } = useRoles()

  return (
    <div className="flex justify-center">
      <div className="flex max-w-[560px] flex-col items-center gap-4">
        <Text className="text-center">
          Pastikan untuk mengunggah dan mengisi detail SK Pengangkatan untuk
          seleksi tahap ini.
        </Text>

        {isDinasUser && (
          <Link href={`/selections/${activeSelection.id}`} passHref>
            <Button size="lg" as="a">
              Unggah SK
              <Icon fontSize="sm" color="inverse" className="ml-1">
                arrow_forward
              </Icon>
            </Button>
          </Link>
        )}
      </div>
    </div>
  )
}

interface PropsSeleksiKSUnggahSK {
  selection: HistorySelection
}

const SeleksiKSLaporKemendagri = ({ selection }: PropsSeleksiKSUnggahSK) => {
  return (
    <div className="flex justify-center">
      <div className="flex max-w-[560px] flex-col items-center gap-4">
        <div className="flex items-center justify-center rounded-full border-2 border-[#4CA375] p-4">
          <div className="absolute flex">
            <Icon color="success" data-testid="icon-check">
              check
            </Icon>
          </div>
        </div>

        <Text className="text-center">
          Seleksi terakhir selesai difinalisasi pada tanggal{' '}
          {formatDate(selection?.endDate)}. Pastikan Anda sudah melaporkan hasil
          seleksi ke Kemendagri.
        </Text>

        <div className="flex gap-3">
          <Link
            href={`/ks/history-selection/details/${selection?.selectionId}`}
            passHref
          >
            <Button color="white" size="lg" as="a">
              Lapor ke Kemendagri
            </Button>
          </Link>

          <Link href={PATH_START_SELECTION} passHref>
            <Button size="lg" as="a">
              Mulai seleksi baru
              <Icon fontSize="sm" color="inverse" className="ml-1">
                arrow_forward
              </Icon>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  )
}

const SectionSeleksiKS = () => {
  const { regionDetail } = useRegionContext()
  const { isDinasUser } = useRoles()
  const { activeSelection, isLoadingActiveSelection, usulan } =
    useActiveSelection()
  const {
    data: responseGetHistorySelection,
    isLoading: isLoadingHistorySelection,
  } = useAPIGetHistorySelection({
    regionId: regionDetail.id,
    regionType: regionDetail.type,
  })

  const lastFinishedSelection = responseGetHistorySelection?.pages[0]?.data[0]
  const shouldLaporKemendagri =
    isLaporKemendagriEnabled &&
    !!lastFinishedSelection &&
    !lastFinishedSelection.kemendagriFileUrl &&
    getIsSelectionEndedAfterKemendagriCutoff(lastFinishedSelection)

  const isShowFinalizationState =
    (activeSelection?.state === 'FINALIZATION' &&
      activeSelection?.statusPejabat === 'UNDEFINE') ||
    (activeSelection?.state === 'PENGUSULAN_BKN' &&
      activeSelection?.statusPejabat === 'PJ' &&
      usulan?.data.status !== 'final')

  const isShowUnggahSKState =
    (!isDinasUser &&
      activeSelection?.state === 'FINALIZATION' &&
      activeSelection?.statusPejabat === 'PJ') ||
    (activeSelection?.state === 'FINALIZATION' &&
      (activeSelection?.statusPejabat === 'DEFINITIF' ||
        (activeSelection?.statusPejabat === 'PJ' &&
          usulan?.data.status === 'final')))

  return (
    <Panel>
      <div className="p-6">
        <Text variant="heading-lg" className="text-center">
          Seleksi Kepala Sekolah
        </Text>
      </div>

      <Separator />

      <div className="p-6">
        {isLoadingActiveSelection || isLoadingHistorySelection ? (
          <div className="flex justify-center" data-testid="loading">
            <Spinner />
          </div>
        ) : (
          <>
            {!activeSelection && !shouldLaporKemendagri && (
              <SeleksiKSNotStarted />
            )}

            {!activeSelection && shouldLaporKemendagri && (
              <SeleksiKSLaporKemendagri selection={lastFinishedSelection} />
            )}

            {activeSelection?.state === 'DOCUMENT_VERIFICATION' && (
              <SeleksiKSDocumentVerification
                activeSelection={activeSelection}
              />
            )}

            {activeSelection?.state === 'PAIRING' && (
              <SeleksiKSPairing activeSelection={activeSelection} />
            )}

            {isShowFinalizationState && (
              <SeleksiKSFinalization activeSelection={activeSelection} />
            )}

            {isShowUnggahSKState && (
              <SeleksiKSUnggahSK activeSelection={activeSelection} />
            )}
          </>
        )}
      </div>
    </Panel>
  )
}

export default SectionSeleksiKS
